import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './utils/api';
// Phase 7 sign-off: profiles email, display_name, short_text.
import { MAX_LENGTHS } from './utils/inputValidation';
import './UserManagement.css';

const ROLE_COLORS = { admin: '#3f51b5', tester: '#ff9800', user: '#78909c' };
const ROLE_LABELS = { admin: 'Org-Admin', tester: 'Sys-Admin', user: 'Viewer' };

export default function SiteAdminUserManager() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [orgOptions, setOrgOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // { mode: 'edit'|'reset', item? }
  const [form, setForm] = useState({ email: '', full_name: '', role: 'user' });
  const [tempPw, setTempPw] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (roleFilter) params.append('role', roleFilter);
      if (orgFilter) params.append('org_id', orgFilter);
      const res = await apiFetch(`/api/v1/admin/all-users?${params}`);
      if (res.ok) {
        const data = await res.json();
        const items = data.items || [];
        setUsers(items);
        setTotal(data.total || 0);

        // Build org dropdown options from returned data
        const orgMap = {};
        items.forEach(u => {
          if (u.org_id && u.org_name) orgMap[u.org_id] = u.org_name;
        });
        setOrgOptions(prev => {
          const merged = { ...prev.reduce((acc, o) => ({ ...acc, [o.id]: o.name }), {}), ...orgMap };
          return Object.entries(merged).map(([id, name]) => ({ id, name }));
        });
      } else {
        setError('Failed to load users');
      }
    } catch { setError('Failed to load users'); }
  }, [page, roleFilter, orgFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filteredUsers = search
    ? users.filter(u =>
        u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.org_name?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const handleUpdate = async () => {
    setError('');
    try {
      const res = await apiFetch(`/api/v1/admin/users/${modal.item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ full_name: form.full_name, email: form.email, role: form.role }),
      });
      if (res.ok) {
        setModal(null);
        setMessage('User updated');
        loadUsers();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Failed to update');
      }
    } catch { setError('Update failed'); }
  };

  const toggleActive = async (u) => {
    const action = u.is_active ? 'Revoke access for' : 'Reactivate';
    const extra = u.is_active ? ' They will be immediately logged out.' : '';
    if (!window.confirm(`${action} ${u.full_name}?${extra}`)) return;
    try {
      const res = u.is_active
        ? await apiFetch(`/api/v1/admin/users/${u.id}`, { method: 'DELETE' })
        : await apiFetch(`/api/v1/admin/users/${u.id}`, {
          method: 'PUT',
          body: JSON.stringify({ is_active: true }),
        });
      // apiFetch DOES NOT THROW ON 4xx/5xx — it returns the response for every
      // status except 401. Without this check the `catch` below only fired on a
      // network failure, so a 403 or 409 left the admin having confirmed
      // "They will be immediately logged out", seen no error, and the user kept
      // their access. A revoke that fails silently is worse than one that
      // errors: the admin believes the access is gone and stops watching.
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(
          (u.is_active ? 'Revoke failed' : 'Reactivate failed')
          + `: ${body.detail || body.error || `HTTP ${res.status}`}`
        );
        return;
      }
      setMessage(u.is_active
        ? `Access revoked for ${u.full_name}`
        : `${u.full_name} reactivated`);
      loadUsers();
    } catch { setError('Action failed'); }
  };

  const resetPassword = async (u) => {
    try {
      const res = await apiFetch(`/api/v1/admin/users/${u.id}/reset-password`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setTempPw(data.temporary_password);
      } else setError('Reset failed');
    } catch { setError('Reset failed'); }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="user-mgmt">
      <div className="um-header">
        <div className="um-header-left">
          <h1>Site Admin User Manager</h1>
          <span className="um-seat-chip">{total} total users</span>
        </div>
      </div>

      {error && <div className="um-error">{error}<button onClick={() => setError('')}>×</button></div>}
      {message && <div className="um-msg">{message}<button onClick={() => setMessage('')}>×</button></div>}
      {tempPw && (
        <div className="um-msg" style={{ background: '#fff3cd', color: '#856404', borderColor: '#f29d88' }}>
          Temporary password: <code style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{tempPw}</code>
          <button onClick={() => { navigator.clipboard.writeText(tempPw); setMessage('Copied!'); }}>Copy</button>
          <button onClick={() => setTempPw(null)}>×</button>
        </div>
      )}

      <div className="um-toolbar">
        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}>
          <option value="">All Roles</option>
          <option value="admin">Org-Admin</option>
          <option value="tester">Sys-Admin</option>
          <option value="user">Viewer</option>
        </select>
        <select value={orgFilter} onChange={e => { setOrgFilter(e.target.value); setPage(1); }}>
          <option value="">All Orgs</option>
          {orgOptions.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <input placeholder="Search by name, email, or org..." value={search} onChange={e => setSearch(e.target.value)} maxLength={MAX_LENGTHS.short_text} />
      </div>

      <table className="um-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Organization</th>
            <th>Role</th>
            {/* "Credits" was users.credit_balance, the legacy per-user ledger.
                It gates nothing on the analyse path, so it could read 0 beside
                someone analysing all day -- and there is no per-user balance to
                show any more: credits are one pool per organisation. The two
                honest numbers are the pool this person spends from and how much
                of it they personally burned this plan month (T12). */}
            <th>Org pool</th>
            <th>Used this period</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map(u => (
            <tr key={u.id} className={u.is_active ? '' : 'inactive-row'}>
              <td className="name-cell">
                {u.full_name}
                {u.is_root && <span className="role-chip" style={{ background: '#b71c1c', marginLeft: 6, fontSize: '0.7rem' }}>ROOT</span>}
                {!u.is_root && u.can_manage_admins && <span className="role-chip" style={{ background: '#6a1b9a', marginLeft: 6, fontSize: '0.7rem' }}>SITE-ADMIN</span>}
              </td>
              <td>{u.email}</td>
              <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{u.org_name || '—'}</td>
              <td><span className="role-chip" style={{ background: ROLE_COLORS[u.role] }}>{ROLE_LABELS[u.role] || u.role}</span></td>
              <td className="credit-cell">{u.org_credits_remaining != null ? u.org_credits_remaining.toLocaleString() : '—'}</td>
              {/* Each org's plan month starts on its own purchase anniversary,
                  not the calendar 1st, so on a cross-org list the window this
                  figure covers differs row by row. Surface it. */}
              <td className="credit-cell" title={u.period_start ? `Since ${u.period_start}` : undefined}>
                {u.credits_used_period != null ? u.credits_used_period.toLocaleString() : '—'}
              </td>
              <td><span className={`status-chip ${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
              <td>{u.last_login ? new Date(u.last_login).toLocaleString() : '—'}</td>
              <td className="actions-cell">
                {!u.is_root && (
                  <>
                    <button className="btn-sm" onClick={() => { setForm({ email: u.email, full_name: u.full_name, role: u.role }); setModal({ mode: 'edit', item: u }); }}>Edit</button>
                    <button className={`btn-sm ${u.is_active ? 'danger' : 'success'}`} onClick={() => toggleActive(u)}>
                      {u.is_active ? 'Revoke' : 'Activate'}
                    </button>
                    {u.is_active && <button className="btn-sm" onClick={() => resetPassword(u)}>Reset PW</button>}
                  </>
                )}
              </td>
            </tr>
          ))}
          {filteredUsers.length === 0 && <tr><td colSpan={9} className="empty-cell">No users found</td></tr>}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="um-pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {/* Edit Modal */}
      {modal && modal.mode === 'edit' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Edit User — {modal.item.org_name}</h2>
            <div className="modal-form">
              <label>Full Name<input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} maxLength={MAX_LENGTHS.display_name} /></label>
              <label>Email<input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} maxLength={MAX_LENGTHS.email} /></label>
              <label>Role
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                  <option value="admin">Org-Admin</option>
                  <option value="tester">Sys-Admin</option>
                  <option value="user">Viewer</option>
                </select>
              </label>
              <div className="btn-row">
                <button className="btn-primary" onClick={handleUpdate}>Save</button>
                <button className="btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The per-user "Adjust Credits" modal lived here. It POSTed to
          /admin/credits/{user_id}/adjust, which writes users.credit_balance --
          a counter no analysis ever spends. A site admin could grant 20 credits
          to a customer on the phone, see a success toast, and have given them
          nothing. Removed with the column it read (T12); the org-pool grant
          that replaces it is T10. */}
    </div>
  );
}
